import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TableDataType, TableSearch } from '@shared/types/table';

@Component({
  selector: 'rav-table-search',
  templateUrl: './table-search.component.html',
  styleUrls: ['./table-search.component.scss']
})
export class TableSearchComponent {
  @Input() dataType: TableDataType;
  @Input() searchLabel: string;
  @Output() newSearch = new EventEmitter<TableSearch[]>(true);

  activeSearches: TableSearch[] = [];
  savedBasicSearch: TableSearch;
  savedAdvancedSearches: TableSearch[] = [];
  isAdvancedSearch = false;
  newSearchNumber = 1;
  advancedSearchesShown: number[] = [0];

  /**
   * Toggles whether we are displaying advanced searching or basic
   * searching. Advanced searches are saved and reloaded when switching back
   * to advanced search.
   */
  toggleAdvancedSearch() {
    if (this.isAdvancedSearch) {
      this.savedAdvancedSearches = this.activeSearches;
      this.activeSearches = [];
      if (this.savedBasicSearch) this.activeSearches = [this.savedBasicSearch];
    } else {
      this.savedBasicSearch = this.activeSearches[0];
      this.activeSearches = this.savedAdvancedSearches;
    }
    this.isAdvancedSearch = !this.isAdvancedSearch;
    this.newSearch.next(this.activeSearches);
  }

  /**
   * Triggered from basic-search component's newBasicSearch event. If a basic search is received,
   * it has to be the only active search since basic searches and advanced searches cannot be entered
   * as the same time.
   *
   * @param newSearch The new search to process
   */
  onNewBasicSearch(newSearch: TableSearch) {
    if (newSearch.value.length > 2) this.activeSearches = [newSearch];
    else this.activeSearches = [];
    this.newSearch.next(this.activeSearches);
  }

  /**
   * Triggered from advanced-search components' newAdvancedSearch event. New searches
   * are processed to determine if an old search parameter should be modified or a new
   * search parameter should be added.
   *
   * @param newSearch The new advanced search to process
   */
  onNewAdvancedSearch(newSearch: TableSearch) {
    if (!this.updateExistingAdvancedSearch(newSearch)) {
      this.addNewAdvancedSearch(newSearch);
    }
  }

  /**
   * Checks to see if the new advanced search is already being used as a search parameter.
   * If it is, the old search parameter is updated.
   *
   * @param newSearch The new advanced search to process
   * @returns if newSearch already existed as a search parameter
   */
  updateExistingAdvancedSearch(newSearch: TableSearch): boolean {
    let foundSearch = false;
    this.activeSearches.forEach((search) => {
      if (!foundSearch && search.field === newSearch.field) {
        if (newSearch.value.length === 0) {
          this.activeSearches = this.activeSearches.filter((item) => item !== search);
        } else {
          search.value = newSearch.value;
        }
        this.newSearch.next(this.activeSearches);
        foundSearch = true;
      }
    });
    return foundSearch;
  }

  /**
   * Adds a new advanced search parameter to activeSearches and sends the updated
   * search parameters to the table component.
   *
   * @param newSearch The new advanced search to add to activeSearches
   */
  addNewAdvancedSearch(newSearch: TableSearch) {
    if (newSearch.value.length > 0) {
      this.activeSearches.push(newSearch);
      this.newSearch.next(this.activeSearches);
    }
  }

  /**
   * Adds a row to the advanced search options when the plus button is clicked.
   */
  addAdvancedSearchOption() {
    this.advancedSearchesShown.push(this.newSearchNumber++);
  }

  /**
   * Removes a particular advanced search option from the view when the option's
   * minus button is clicked. Triggered via the advanced-search components' searchRemoved
   * event.
   *
   * @param removedSearchNumber The index of advanced search option to remove
   */
  removeAdvancedSearchOption(removedSearchNumber: number) {
    if (this.advancedSearchesShown.length <= 1) return;
    this.advancedSearchesShown.splice(removedSearchNumber, 1);
  }
}
